import type { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type ITenantRepository from '@repositories/tenantRepository/ITenantRepository';
import type {
  Filters,
  TenantAddMemberRq,
  TenantMemberFilter,
  TenantMemberRoleUpdateRq,
  Tenant,
  TenantUpdateWithFileRq,
  TenantMemberStatusUpdateDTO,
  MyTenantFilter,
  TenantList,
  TenantSimple,
} from '@repositories/tenantRepository/tenant.types';
import type { ConnectionResp } from '@repositories/subscriptionConnectionRepository/subscriptionConnection.types';
import type { TenantMemberSimple } from '@repositories/userRepository/user.types';
import type { Page } from '@type/Page';
import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import { TenantMemberSimpleModel } from '@repositories/tenantMemberRepository/models/TenantMemberSimpleModel';
import { ConnectionRespModel } from '@repositories/subscriptionConnectionRepository/models/ConnectionRespModel';
import { TenantListModel } from '@repositories/tenantRepository/models/TenantListModel';
import { TenantModel } from '@repositories/tenantRepository/models/TenantModel';
import { TenantSimpleModel } from '@repositories/tenantRepository/models/TenantSimpleModel';

const tenantRepo = repository.get<ITenantRepository>(KEYS.TENANT_REPOSITORY);

export const tenantQueryKey = {
  all: ['Tenant'] as const,
  lists: () => [...tenantQueryKey.all, 'list'] as const,
  list: (filters: Filters) => [...tenantQueryKey.lists(), { ...filters }] as const,
  myList: (filters?: MyTenantFilter) => [...tenantQueryKey.lists(), 'my', { ...filters }] as const,
  details: () => [...tenantQueryKey.all, 'detail'] as const,
  detail: (id: string) => [...tenantQueryKey.details(), id] as const,
};

export const tenantMemberQueryKey = {
  all: ['TenantMember'] as const,
  lists: () => [...tenantMemberQueryKey.all, 'list'] as const,
  list: (id: string, filters: TenantMemberFilter) => [...tenantMemberQueryKey.lists(), id, { ...filters }] as const,
  listInfinite: (id: string, filters: TenantMemberFilter) =>
    [...tenantMemberQueryKey.list(id, filters), 'infinite'] as const,
  details: () => [...tenantMemberQueryKey.all, 'detail'] as const,
  detail: (id: string) => [...tenantMemberQueryKey.details(), id] as const,
};

export const useGetTenantList = (
  queries?: Filters,
  options?: Omit<UseQueryOptions<Page<TenantList>, AxiosError, Page<TenantListModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectTenantList = useCallback(
    (data: Page<TenantList>) => ({
      ...data,
      content: data.content.map(tenant => new TenantListModel(tenant)),
    }),
    [],
  );

  return useQuery<Page<TenantList>, AxiosError, Page<TenantListModel>>({
    queryKey: tenantQueryKey.list(queries ?? {}),
    queryFn: () => tenantRepo.getList(queries),
    select: selectTenantList,
    ...options,
  });
};

export const useGetMyTenantList = (
  queries?: MyTenantFilter,
  options?: Omit<UseQueryOptions<TenantSimple[], AxiosError, TenantSimpleModel[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectMyTenantList = useCallback(
    (data: TenantSimple[]) => data.map(tenantSimple => new TenantSimpleModel(tenantSimple)),
    [],
  );

  return useQuery<TenantSimple[], AxiosError, TenantSimpleModel[]>({
    queryKey: tenantQueryKey.myList(queries),
    queryFn: () => tenantRepo.getMyTenants(queries),
    select: selectMyTenantList,
    ...options,
  });
};

export const useGetTenant = (
  id: TenantId,
  options?: Omit<UseQueryOptions<Tenant, AxiosError, TenantModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectTenant = useCallback((data: Tenant) => new TenantModel(data), []);

  return useQuery<Tenant, AxiosError, TenantModel>({
    queryKey: tenantQueryKey.detail(id),
    queryFn: () => tenantRepo.getOne(id),
    select: selectTenant,
    ...options,
    enabled: !!id && (options?.enabled !== undefined ? options.enabled : true),
  });
};

export const useGetTenantMembers = (
  id: TenantId,
  queries?: TenantMemberFilter,
  options?: Omit<
    UseQueryOptions<Page<TenantMemberSimple>, AxiosError, Page<TenantMemberSimpleModel>>,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  const selectTenantMembers = useCallback(
    (data: Page<TenantMemberSimple>) => ({
      ...data,
      content: data.content.map(member => new TenantMemberSimpleModel(member)),
    }),
    [],
  );

  const result = useQuery<Page<TenantMemberSimple>, AxiosError, Page<TenantMemberSimpleModel>>({
    queryKey: tenantMemberQueryKey.list(id, queries ?? {}),
    queryFn: () => tenantRepo.getMembers(id, queries),
    select: selectTenantMembers,
    cacheTime: 100 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
    ...options,
  });

  const initialData = useMemo(() => ({ content: [], totalElements: 0 }), []);
  return { ...result, data: result.data ?? initialData };
};

export const useGetTenantMember = (
  id: TenantId,
  userId: UserId,
  options?: Omit<UseQueryOptions<TenantMemberSimple, AxiosError, TenantMemberSimpleModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectTenantMember = useCallback((data: TenantMemberSimple) => new TenantMemberSimpleModel(data), []);

  const result = useQuery<TenantMemberSimple, AxiosError, TenantMemberSimpleModel>({
    queryKey: tenantMemberQueryKey.detail(userId),
    queryFn: () => tenantRepo.getMember(id, userId),
    select: selectTenantMember,
    ...options,
    cacheTime: 100 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });

  return result;
};

export const useGetTenantMembersInfinite = (
  id: TenantId,
  queries?: Omit<TenantMemberFilter, 'page'>,
  options?: UseInfiniteQueryOptions<Page<TenantMemberSimpleModel>>,
) =>
  useInfiniteQuery({
    queryKey: tenantMemberQueryKey.listInfinite(id, queries ?? {}),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await tenantRepo.getMembers(id, { ...queries, page: pageParam });
      return { ...result, content: result.content.map(member => new TenantMemberSimpleModel(member)), pageParam };
    },
    ...options,
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });

export const useAddTenantMembers = () => {
  const queryClient = useQueryClient();

  const result = useMutation(
    async (rqData: TenantAddMemberRq[]) => {
      const result = await tenantRepo.addMembers(rqData[0].tenantId, rqData);
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
      },
    },
  );

  return { ...result };
};

export const useDeleteTenantMember = () => {
  const queryClient = useQueryClient();

  const result = useMutation(
    async ({ id, userId }: { id: TenantId; userId: UserId }): Promise<void> => {
      await tenantRepo.deleteMember(id, userId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
      },
    },
  );

  return { ...result };
};

export const useUpdateTenantMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: TenantMemberRoleUpdateRq) => {
      const { userId } = await tenantRepo.updateMemberRole(data.tenantId, data);
      return userId;
    },
    {
      onSuccess: userId => {
        queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.detail(userId) });
        queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
      },
    },
  );
};

export const useDeleteTenant = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: TenantId): Promise<void> => {
      const result = await tenantRepo.delete(id);
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: tenantQueryKey.lists() });
      },
    },
  );
};

export const useUpdateTenantLogo = () => {
  const queryClient = useQueryClient();

  const result = useMutation({
    mutationFn: async ({ id, tenant, logoImage }: TenantUpdateWithFileRq) => {
      const result = await tenantRepo.updateWithFile(id, tenant, logoImage);
      return result.id;
    },
    onSuccess: (id: string) => {
      queryClient.invalidateQueries({ queryKey: tenantQueryKey.list({ id }) });
      queryClient.invalidateQueries({ queryKey: tenantQueryKey.detail(id) });
    },
  });

  return result;
};

export const useUpdateTenantMemberStatus = () => {
  const queryClient = useQueryClient();

  type MutationVariable = {
    tenantId: string;
    userId: string;
    rqData: TenantMemberStatusUpdateDTO;
  };

  return useMutation({
    mutationFn: async (variable: MutationVariable) => {
      const result = await tenantRepo.updateMemberStatus(variable.tenantId, variable.userId, variable.rqData);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
    },
  });
};

export const useDeleteTenantMemberUsingFlag = () => {
  const queryClient = useQueryClient();

  type MutationVariable = {
    tenantId: string;
    userId: string;
  };

  return useMutation({
    mutationFn: async (variable: MutationVariable) => {
      const result = await tenantRepo.deleteMemberUsingFlag(variable.tenantId, variable.userId);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantMemberQueryKey.lists() });
    },
  });
};

export const useGetSMPConnections = (
  tenantId: string,
  options?: Omit<UseQueryOptions<ConnectionResp, AxiosError, ConnectionRespModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectSMPConnections = useCallback((data: ConnectionResp) => new ConnectionRespModel(data), []);

  const result = useQuery<ConnectionResp, AxiosError, ConnectionRespModel>({
    queryKey: ['SMPConnection', tenantId],
    queryFn: () => tenantRepo.getSMPConnections({ tenantId }),
    select: selectSMPConnections,
    ...options,
    cacheTime: 100 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });

  return result;
};
